<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep, WritableDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';
import type { DefDialogState, DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useCssModule,
  useTemplateRef,
  watch,
} from 'vue';
import _compact from 'lodash/compact';

// import { successDelay } from '@/utils/funcs';
import KBtn from '@/components/btns/KBtn.vue';
import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import KTooltip from '@/components/tooltips/KTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import SmartDispatchJobMenuVList from '@/components/vLists/SmartDispatchJobMenuVList.vue';
import useTable from '@/composables/useTable';
import { useDialogStore } from '@/stores/useDialogStore';
import { useDispatchDashboardViewStore } from '@/stores/useDispatchDashboardViewStore';
import { useHotkeysStore } from '@/stores/useHotkeysStore';
import * as dispatchHelpers from '@/helpers/dispatch';
import { fetchPostJobAllocationBulk } from '@/helpers/fetchers';
import * as hotkeys from '@/constants/hotkeys';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  isLocked: boolean;
  jobId: number;
  jobDate: string;
  time: string;
  timeTooltip: string;
  clientIcon: string | null | undefined;
  clientIconTooltip: string | null | undefined;
  clientCode: string;
  clientName: string;
  from: string;
  to: string;
  service: string;
  notes: string;
  isReadyToAllocate: boolean;
  originalJob: ReadonlyDeep<openapi.DespatchJobsContentOpenapi[0]>;
}

export interface Model {
  active: number;
  select: Record<string, boolean>;
}

export interface Props {
  loading?: boolean;
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const model = defineModel<Model>({
  default: () => ({
    active: NaN,
    select: {},
  }),
});

const styles = useCssModule();

const isRenderCounter = ref(0);

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const _sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const sortedData = ref<DataItem[]>([]);

const isUnmounted = ref(false);
const renderedSortedData = ref<ReadonlyDeep<DataItem[]>>([]);

async function renderSortedData(
  newSortedData: ReadonlyDeep<DataItem[]>,
): Promise<void> {
  sortedData.value = [];
  await new Promise((resolve) => setTimeout(resolve, 50));
  renderedSortedData.value = newSortedData;
  for (let index = 0; index < newSortedData.length; index++) {
    if (isUnmounted.value || renderedSortedData.value !== newSortedData) return;
    sortedData.value[index] = newSortedData[index] as WritableDeep<DataItem>;
    if (index < 3) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }
  renderedSortedData.value = [];
}

watch(
  _sortedData,
  async (newValues, oldValues) => {
    isRenderCounter.value++;
    await renderSortedData(newValues).finally(() => {
      isRenderCounter.value--;
    });
  },
  { immediate: true },
);

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'jobId',
      title: 'Job ID',
    },
    {
      value: 'jobDate',
      title: 'Date',
    },
    {
      value: 'time',
      title: 'Time',
    },
    {
      value: 'client',
      title: 'Client',
    },
    {
      value: 'from',
      title: 'From',
    },
    {
      value: 'to',
      title: 'To',
    },
    {
      value: 'service',
      title: 'SVC',
    },
    {
      value: 'notes',
      title: 'Notes',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 2;
});

const activeAllocs = ref<Record<string, boolean>>({});
const loadingAllocs = ref<Record<string, boolean>>({});
const activeDots = ref<Record<string, boolean>>({});
const activeMainDots = ref(false);

// TODO rename to SmartDispatchJobsTable
const dialogStore = useDialogStore();
const dispatchDashboardViewStore = useDispatchDashboardViewStore();
const hotkeysStore = useHotkeysStore();

// On the Allocated tab every job is already allocated, so the header Alloc
// button flips to "Unalloc" (bulk deallocate).
const isAllocatedTab = computed(() => {
  return dispatchDashboardViewStore.query.tab === 'allocated';
});

async function handleAllocClick(id: number): Promise<void> {
  const dataItem = props.data.find((_dataItem) => _dataItem.id === id);
  if (!dataItem) return;

  // TODO
  // maybe loading doesn't need here because of dialogs or show loading state on Alloc buttons even if it was clicked from menu
  // if (loadingAllocs.value[id]) return;
  // loadingAllocs.value[id] = true;
  // await successDelay().finally(() => {
  //   loadingAllocs.value[id] = false;
  // });

  if (!dataItem.isReadyToAllocate) return;

  // if (activeAllocs.value[id]) return;
  activeAllocs.value[id] = true;

  const jobs = [];
  const job = getJobFromId(id);
  if (job) jobs.push(job);
  if (!jobs.length) return;

  const dialogName: DialogName = 'dispatchAssignDriver';
  dialogStore
    .open(dialogName, { jobs })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        activeAllocs.value[id] = false;
      },
    );
}

function handleSelectedAllocClick() {
  if (!selectCount.value) return;
  const jobs = getSelectedJobs();
  if (!jobs.length) return;

  const dialogName: DialogName = 'dispatchAssignDriver';
  dialogStore.open(dialogName, { jobs, action: 'allocate' });
}

function handleSelectedDeallocClick() {
  if (!selectCount.value) return;
  const dataItems = getSelectedDataItems();
  if (!dataItems.length) return;
  openDeallocConfirm(dataItems);
}

function handleDeallocClick(id: number): void {
  const dataItem = props.data.find((_dataItem) => _dataItem.id === id);
  if (!dataItem) return;
  openDeallocConfirm([dataItem]);
}

function openDeallocConfirm(dataItems: DataItem[]): void {
  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore.open(dialogName, {
    confirm: async () => {
      // Deallocate each job (the action follows its allocation state); one
      // summary toast.
      await fetchPostJobAllocationBulk(
        dataItems.map((dataItem) =>
          dispatchHelpers.getDeallocateActionItem(dataItem.originalJob),
        ),
      );
    },
    type: 'deallocate',
    jobs: dataItems.map((dataItem) => ({
      jobId: dataItem.jobId,
      jobDate: dataItem.jobDate,
    })),
  });
}

// TODO maybe better naming or move `activeDots` to store and use it from `SmartDispatchJobMenuVList`, also need to clear it from redundant leftover ids
function handleDone(id: number) {
  activeDots.value[id] = false;
}
function handleMainDone() {
  activeMainDots.value = false;
}

const isCommonCheckbox = ref(false);
const isCommonCheckboxIndeterminate = ref(false);

const rootEl = useTemplateRef('root');

const allIds = computed(() => {
  return props.data.map((item) => item.id);
});

const selectCount = computed(() => {
  let _selectCount = 0;
  allIds.value.forEach((id: number) => {
    if (model.value.select[id]) _selectCount++;
  });
  return _selectCount;
});

function clearSelect() {
  for (const id of Object.getOwnPropertyNames(model.value.select)) {
    model.value.select[id] = false;
  }
}

function updateDOMActive() {
  const $root = rootEl.value;
  if (!$root) return;
  if (Number.isNaN(model.value.active)) {
    const activeTrs = $root.querySelectorAll('.' + styles.isActiveTr);
    for (const $tr of activeTrs) {
      $tr.classList.remove(styles.isActiveTr);
    }
  } else {
    const $currentTr = $root.querySelector(
      '[data-rowid="' + model.value.active + '"]',
    );
    if (!$currentTr) return;
    $currentTr.classList.add(styles.isActiveTr);
  }
}

function handleIsCommonCheckboxChange() {
  if (isCommonCheckbox.value) {
    setTimeout(() => {
      allIds.value.forEach((id: number) => {
        model.value.select[id] = true;
      });
    }, 0);
  } else {
    setTimeout(() => {
      clearSelect();
    }, 0);
  }
}

function handleTrClick(event: MouseEvent, id: number) {
  if (!event.target) return;
  if (model.value.active === id) return;
  const $target = event.target as HTMLElement;
  if ($target.tagName === 'LABEL') return;
  if ($target.tagName === 'INPUT') return;
  if ($target.tagName === 'A') return;
  if ($target.tagName === 'button') return;
  if ($target.closest('tbody > tr LABEL')) return;
  if ($target.closest('tbody > tr menu')) return;
  if ($target.closest('tbody > tr a')) return;
  // if ($target.getAttribute('data-testid') === 'jobIdAndIconLinkUnit') return;
  const $currentTr = $target.closest('tbody > tr');
  if (!$currentTr) return;
  // clearSelect(); // maybe making the item active without clearing the selected items is just better
  // HACK toggle model.value.active with nextTick since we update DOM manually
  model.value.active = NaN;
  void nextTick(() => {
    model.value.active = id;
  });
}

function getJobFromId(id: number): { jobId: number; jobDate: string } | null {
  const dataItem = props.data.find((_dataItem) => {
    return _dataItem.id === id;
  });
  if (!dataItem) return null;
  return {
    jobId: dataItem.jobId,
    jobDate: dataItem.jobDate,
  };
}

function getSelectedJobs(): { jobId: number; jobDate: string }[] {
  const selectedIds = [];
  for (const prop of Object.getOwnPropertyNames(model.value.select)) {
    if (model.value.select[prop]) selectedIds.push(parseInt(prop));
  }
  return _compact(
    selectedIds.map((selectedId) => {
      return getJobFromId(selectedId);
    }),
  );
}
function getSelectedDataItems(): DataItem[] {
  const selectedIds = [];
  for (const prop of Object.getOwnPropertyNames(model.value.select)) {
    if (model.value.select[prop]) selectedIds.push(parseInt(prop));
  }
  return _compact(
    selectedIds.map((selectedId) => {
      return props.data.find((dataItem) => dataItem.id === selectedId) ?? null;
    }),
  );
}

function handleAllocateSelectedJobsHotkey(event: KeyboardEvent) {
  let jobs = [];
  if (selectCount.value > 0) {
    jobs = getSelectedJobs();
  } else {
    const job = getJobFromId(model.value.active);
    if (job) jobs.push(job);
  }
  if (!jobs.length) return;

  const dialogName: DialogName = 'dispatchAssignDriver';
  dialogStore.open(dialogName, { jobs });
}

onMounted(() => {
  hotkeysStore.add(
    hotkeys.ALLOCATE_SELECTED_JOBS,
    handleAllocateSelectedJobsHotkey,
    { inputs: true }, // should work when checkbox is selected
    hotkeys.comments.ALLOCATE_SELECTED_JOBS,
  );
});

onUnmounted(() => {
  hotkeysStore.remove(hotkeys.ALLOCATE_SELECTED_JOBS);
});

watch(
  () => props.data,
  (newValues, oldValues) => {
    model.value.active = NaN;
    for (const prop of Object.getOwnPropertyNames(model.value.select)) {
      if (allIds.value.includes(parseInt(prop))) {
        model.value.select[prop] = false;
      } else {
        /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
        delete model.value.select[prop];
      }
    }
  },
  { immediate: true },
);

watch(
  () => model.value.active,
  (newValues, oldValues) => {
    updateDOMActive();
  },
  { immediate: true },
);

watch(
  () => model.value.select,
  (newValues, oldValues) => {
    if (!selectCount.value) {
      isCommonCheckbox.value = false;
      isCommonCheckboxIndeterminate.value = false;
    } else if (selectCount.value === allIds.value.length) {
      isCommonCheckbox.value = true;
      isCommonCheckboxIndeterminate.value = false;
    } else if (selectCount.value !== allIds.value.length) {
      isCommonCheckbox.value = false;
      isCommonCheckboxIndeterminate.value = true;
    }
  },
  { deep: true },
);

// TODO
// const isSomeSelectedReadyToAllocate = computed(() => {
// });
</script>

<template>
  <table
    ref="root"
    :class="[
      tableStyles.table,
      tableStyles.hovered,
      tableStyles.bordered,
      $style.dispatchJobTable,
    ]"
    data-testid="dispatchJobTable"
  >
    <thead>
      <tr>
        <th :class="$style.checkboxTh">
          <label :class="$style.checkboxLabel">
            <KCheckboxField
              v-if="props.loading"
              hide-details
              class="my-n3"
              size="sm"
              disabled
            />
            <KCheckboxField
              v-else
              v-model="isCommonCheckbox"
              :indeterminate="isCommonCheckboxIndeterminate"
              hide-details
              class="my-n3"
              size="sm"
              :disabled="!headersData.length || !sortedData?.length"
              @change="handleIsCommonCheckboxChange"
            />
          </label>
        </th>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
        <th :class="$style.actionsTh">
          <menu :class="$style.actionsMenuList">
            <li :class="$style.actionsMenuListItem">
              <KTooltip v-if="isAllocatedTab" location="top" :offset="0">
                <div>Unalloc selected jobs</div>
                <template #activator="{ props: tooltipProps }">
                  <KBtn
                    v-bind="tooltipProps"
                    size="xs"
                    :loading="false"
                    :disabled="!selectCount || props.loading"
                    :class="$style.allocBtn"
                    @click="handleSelectedDeallocClick"
                  >
                    Unalloc
                  </KBtn>
                </template>
              </KTooltip>
              <KTooltip v-else location="top" :offset="0">
                <div>
                  {{ hotkeys.comments.ALLOCATE_SELECTED_JOBS }}
                  <v-hotkey inline :keys="hotkeys.ALLOCATE_SELECTED_JOBS" />
                </div>
                <template #activator="{ props: tooltipProps }">
                  <KBtn
                    v-bind="tooltipProps"
                    size="xs"
                    :loading="false"
                    :disabled="!selectCount || props.loading"
                    :class="$style.allocBtn"
                    @click="handleSelectedAllocClick"
                  >
                    Alloc
                  </KBtn>
                </template>
              </KTooltip>
            </li>
            <li :class="$style.actionsMenuListItem">
              <v-menu
                v-model="activeMainDots"
                :close-on-content-click="false"
                location="right"
                scroll-strategy="none"
                transition="slide-y-transition"
              >
                <template #activator="{ props: menuProps }">
                  <KBtn
                    :disabled="!selectCount || props.loading"
                    size="xs"
                    variant="text"
                    icon="mdi-dots-vertical"
                    v-bind="menuProps"
                  ></KBtn>
                </template>
                <SmartDispatchJobMenuVList @done="handleMainDone" />
              </v-menu>
            </li>
          </menu>
        </th>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-full p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else data-testid="dispatchJobTableBody">
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <!-- HACK for better performance update DOM manually instead of Vue
        <tr
          :class="{ [$style.isActiveTr]: dataItem.id === model.active }"
          @click="handleTrClick($event, dataItem.id)"
        >
        -->
        <tr
          :data-rowid="dataItem.id"
          @click="handleTrClick($event, dataItem.id)"
        >
          <td :class="$style.checkboxTd">
            <label :class="$style.checkboxLabel">
              <v-lazy>
                <KCheckboxField
                  v-model="model.select[dataItem.id]"
                  hide-details
                  class="my-n3"
                  size="sm"
                />
              </v-lazy>
            </label>
          </td>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="dataItem.jobId"
              :job-date="dataItem.jobDate"
            />
            <template v-if="dataItem.isLocked">
              <br />
              <KTooltip location="top" :offset="7">
                Job is locked
                <template #activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    :class="$style.lockIcon"
                    icon="mdi-lock"
                    size="xs"
                  ></v-icon>
                </template>
              </KTooltip>
            </template>
          </td>
          <td :class="$style.jobDateTd">
            {{ dataItem.jobDate }}
          </td>
          <td :class="$style.timeTd">
            <span>
              <KParentTooltip>{{ dataItem.timeTooltip }}</KParentTooltip>
              {{ dataItem.time }}
            </span>
          </td>
          <td :class="$style.clientTd">
            <span>
              <KParentTooltip location="top">
                {{ dataItem.clientName }}
              </KParentTooltip>
              {{ dataItem.clientCode }}
            </span>
            <sup v-if="dataItem.clientIcon" :class="$style.sup">
              {{ dataItem.clientIcon }}
              <KParentTooltip location="right">
                {{ dataItem.clientIconTooltip }}
              </KParentTooltip>
            </sup>
          </td>
          <td :class="$style.fromTd">
            <span>
              <KParentTooltip location="top">
                {{ dataItem.from }}
              </KParentTooltip>
              {{ dataItem.from }}
            </span>
          </td>
          <td :class="$style.toTd">
            <span>
              <KParentTooltip location="top">
                {{ dataItem.to }}
              </KParentTooltip>
              {{ dataItem.to }}
            </span>
          </td>
          <td :class="$style.serviceTd">
            {{ dataItem.service }}
          </td>
          <td :class="$style.notesTd">
            {{ dataItem.notes }}
          </td>
          <td :class="$style.actionsTd">
            <v-lazy>
              <menu :class="$style.actionsMenuList">
                <li :class="$style.actionsMenuListItem">
                  <KTooltip v-if="isAllocatedTab" location="top" :offset="0">
                    <div>Unalloc Job</div>
                    <template #activator="{ props: tooltipProps }">
                      <KBtn
                        v-bind="tooltipProps"
                        size="xs"
                        variant="outlined"
                        :disabled="dataItem.isLocked || props.loading"
                        :class="$style.allocBtn"
                        @click="handleDeallocClick(dataItem.id)"
                      >
                        Unalloc
                      </KBtn>
                    </template>
                  </KTooltip>
                  <KTooltip v-else location="top" :offset="0">
                    <div>
                      Allocate Job
                      <v-hotkey inline :keys="hotkeys.ALLOCATE_SELECTED_JOBS" />
                    </div>
                    <template #activator="{ props: tooltipProps }">
                      <KBtn
                        v-bind="tooltipProps"
                        size="xs"
                        variant="outlined"
                        :loading="loadingAllocs[dataItem.id]"
                        :active="activeAllocs[dataItem.id]"
                        :disabled="!dataItem.isReadyToAllocate"
                        :class="$style.allocBtn"
                        @click="handleAllocClick(dataItem.id)"
                      >
                        Alloc
                      </KBtn>
                    </template>
                  </KTooltip>
                </li>
                <li :class="$style.actionsMenuListItem">
                  <v-menu
                    v-model="activeDots[dataItem.id]"
                    :close-on-content-click="false"
                    location="right"
                    scroll-strategy="none"
                    transition="slide-y-transition"
                  >
                    <template #activator="{ props: menuProps }">
                      <KBtn
                        size="xs"
                        variant="text"
                        icon="mdi-dots-vertical"
                        v-bind="menuProps"
                      ></KBtn>
                    </template>
                    <SmartDispatchJobMenuVList
                      :dataItem="dataItem"
                      @done="handleDone"
                    />
                  </v-menu>
                </li>
              </menu>
            </v-lazy>
          </td>
        </tr>
      </template>
    </tbody>
    <tr v-if="isRenderCounter && !props.loading">
      <td :colspan="foldColspan" class="!h-24 p-0">
        <TableLoader />
      </td>
    </tr>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:color';
@use '@/styles/colors';
@use '@/styles/variables';
@use '@/styles/table.module.scss' as tableStyles;

.dispatchJobTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: 0;
      z-index: 1;
    }
  }
}
  .checkboxTh {
    min-width: 40px;
    width: 40px;
    padding: 0 !important;
  }
  .jobIdTh {
    min-width: 87px;
    width: 87px;
  }
  .jobDateTh {
    min-width: 95px;
    width: 95px;
  }
  .timeTh {
    min-width: 90px;
  }
  .clientTh {
  }
  .fromTh {
    min-width: 12%;
    width: 12%;
  }
  .toTh {
    min-width: 12%;
    width: 12%;
  }
  .serviceTh {
    min-width: 90px;
    width: 90px;
  }
  .notesTh {
    min-width: 80px;
  }
  .actionsTh {
    min-width: 111px;
    width: 111px;
    white-space: nowrap;
    text-align: center;
  }

  .checkboxTd {
    padding: 0 !important;
  }
    .checkboxLabel {
      width: 42px;
      margin-right: -2px;
      margin-top: -1px;
      margin-bottom: -1px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  .jobIdTd {
    padding-right: 0 !important;
  }
    .lockIcon {
      color: colors.$color-text-tertiary;
    }
  .jobDateTd {
    white-space: nowrap;
  }
  .timeTd {
  }
  .clientTd {
    white-space: nowrap;
  }
    .sup {
      font-size: 80%;
      position: relative;
      top: -6px;
    }
  .fromTd {
    word-break: break-word;
  }
  .toTd {
    word-break: break-word;
  }
  .serviceTd {
  }
  .notesTd {
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
        .allocBtn {
          width: 70px;

          :global {
            .v-btn__append {
              display: none;
            }
          }
        }

.dispatchJobTable > tbody > tr {
  cursor: pointer;
}

tr.isActiveTr {
  & > td {
    background-color: colors.$color-brand-200;
  }
  &:hover > td {
    background-color: color.mix(colors.$color-brand-200, color.adjust(colors.$color-blue-50, $alpha: -0.4)) !important;
  }
}

</style>
